/**
 * @ccheng/ui
 * 共用 UI 元件庫。B1：DataTable v1 + AppShell（製作順序規劃.md §11、業主裁決紀錄.md 裁決 6-3）。
 */

export function Placeholder({ label }: { label: string }) {
  return <div data-testid="ccheng-ui-placeholder">@ccheng/ui: {label}</div>;
}

// ---- 基本元件 ----
export { Button } from "./components/Button";
export type { ButtonProps, ButtonVariant } from "./components/Button";
export { Badge } from "./components/Badge";
export type { BadgeProps, BadgeVariant } from "./components/Badge";
export { ChannelTag } from "./components/ChannelTag";
export type { ChannelTagProps } from "./components/ChannelTag";
export { ThemeToggle, applyTheme, readStoredTheme, THEME_INIT_SCRIPT, THEME_STORAGE_KEY } from "./components/ThemeToggle";
export type { ThemeToggleProps, ThemeMode } from "./components/ThemeToggle";
export { Modal } from "./components/Modal";
export { ConfirmDialog } from "./components/ConfirmDialog";
export type { ConfirmDialogProps, ConfirmLevel } from "./components/ConfirmDialog";
export type { ModalProps } from "./components/Modal";
export { Tabs } from "./components/Tabs";
export type { TabsProps, TabItem } from "./components/Tabs";
export { ToastProvider, useToast } from "./components/Toast";
export type { ToastItem, ToastVariant, ShowToastOptions } from "./components/Toast";
export { Alert } from "./components/Alert";
export type { AlertProps, AlertVariant, AlertRole } from "./components/Alert";
export { FormFieldError, fieldErrorProps } from "./components/FormFieldError";
export type { FormFieldErrorProps } from "./components/FormFieldError";

// ---- LoadingOverlay（全站載入遮罩，2026-09-05 業主指示）----
export {
  LoadingOverlayProvider,
  useLoadingOverlay,
  LOADING_OVERLAY_DELAY_MS,
  LOADING_OVERLAY_MIN_VISIBLE_MS,
  LOADING_OVERLAY_LONG_WAIT_MS,
} from "./LoadingOverlay/LoadingOverlay";
export type {
  LoadingOverlayApi,
  LoadingOverlayTexts,
  LoadingOverlayProviderProps,
} from "./LoadingOverlay/LoadingOverlay";
/* 模組級計數器：供 apiClient 這類「不在 React 世界」的呼叫端使用（見 store.ts）。 */
export {
  beginLoading,
  endLoading,
  trackLoading,
  getPendingCount,
  subscribeLoading,
  resetLoading,
} from "./LoadingOverlay/store";

// ---- DataTable ----
export { DataTable, RANGE_SEP } from "./DataTable/DataTable";
export type {
  ColumnDef,
  ColumnFormat,
  ColumnKind,
  ActionItem,
  FilterOption,
  SortState,
  SortDirection,
  DataTableProps,
  DataTableVariant,
  DataTableDensity,
  DataChannel,
  ChannelBadge,
  PaginationMode,
  ServerTableState,
} from "./DataTable/types";
export {
  formatDate,
  formatDateTime,
  formatCurrency,
  formatBoolean,
  formatText,
  plainFormattedValue,
  renderFormattedCell,
} from "./DataTable/formatters";
export { buildCsv, downloadCsv } from "./DataTable/csv";

// ---- RemoteSelect（遠端搜尋下拉，舊站 kartik Select2 等價）----
export { RemoteSelect } from "./RemoteSelect/RemoteSelect";
export type {
  RemoteOption,
  RemoteSelectProps,
  RemoteSelectTexts,
  RemoteOptionRenderState,
} from "./RemoteSelect/types";

// ---- AppShell ----
export { AppShell } from "./AppShell/AppShell";
export type { AppShellProps } from "./AppShell/AppShell";
export { Sidebar } from "./AppShell/Sidebar";
export type { SidebarProps } from "./AppShell/Sidebar";
export { Topbar } from "./AppShell/Topbar";
export type { TopbarProps } from "./AppShell/Topbar";
export { LoginLayout } from "./AppShell/LoginLayout";
export type { LoginLayoutProps } from "./AppShell/LoginLayout";
export { PageHeader } from "./AppShell/PageHeader";
export type { PageHeaderProps } from "./AppShell/PageHeader";
export type { NavItem, NavGroup, NavSpec, CurrentUser } from "./AppShell/types";

// ---- Demo（頁面接線範本 + 測試用） ----
export { DataTableDemo } from "./demo/DataTableDemo";
export { demoNav, demoFaqRows, buildDemoColumns } from "./demo/fixtures";

/* 訊息文案工具：規範 §6-2 的名稱截斷。先前散成 4 份獨立實作，其中一份會切斷
   代理對；收斂成單一實作並以測試釘住，見 utils/messageName.ts 的說明。 */
export { messageName, MESSAGE_NAME_MAX_CHARS } from "./utils/messageName";
